'use client';
import Image from 'next/image';
import { useState } from 'react';

import { useAnswerSrQuestionMutation, useGetSrQuestionsQuery } from '../../api';
import { Button, Modal } from '../../ui';

export const SupplierRequestAnswerQuestionsModal = ({
  requestId,
  closeModal,
}: {
  requestId: string;
  closeModal: () => void;
}) => {
  const {
    data: supplierRequestQuestions,
    refetch: refetchSrQuestions,
    isLoading,
  } = useGetSrQuestionsQuery(requestId);
  const [answerQuestions, { isLoading: isAnsweringQuestions }] =
    useAnswerSrQuestionMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  let initialAnswers: { [questionId: string]: string } = {};
  supplierRequestQuestions?.forEach((question: any) => {
    if (!question.answered) {
      initialAnswers[question.questionId] = '';
    }
  });
  const [answers, setAnswers] = useState<{
    [questionId: string]: string;
  }>(initialAnswers);

  const handleCloseModal = () => {
    closeModal();
  };

  const handleSubmitAnswers = async () => {
    for (const answer of Object.entries(answers)) {
      await answerQuestions({
        id: requestId,
        questionId: answer[0],
        answer: answer[1],
      });
    }
    refetchSrQuestions();
    setShowConfirmation(true);
  };

  return !showConfirmation ? (
    <Modal
      title="Supplier Questions"
      icon={'/icons/question.svg'}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex gap-8">
        <div className="flex flex-col gap-8 flex-1">
          <div className="flex flex-col gap-2">
            <span className="text-2xl font-bold text-text-primary">
              Questions for Supplier
            </span>
            <span className="text-base font-normal text-text-secondary">
              Before opting in, the supplier has some questions to ensure they
              can meet your requirements effectively. Providing answers will
              help them determine if they can be a suitable fit for your
              supplier needs.
            </span>
          </div>
          {supplierRequestQuestions && supplierRequestQuestions.length > 0 && (
            <div className="flex flex-col gap-4 transition ease-in-out delay-150 max-h-96 overflow-auto">
              {supplierRequestQuestions
                .filter((question: any) => !question.answered)
                .map((question: any) => (
                  <div key={question.questionId} className="space-y-4">
                    <div className="flex justify-start items-end gap-2">
                      <Image
                        src="/icons/avatar-cvs.svg"
                        width={40}
                        height={40}
                        alt="Avatar"
                      />
                      <span className="bg-primary-100 rounded-2xl rounded-bl-md p-4">
                        {question.question}
                      </span>
                    </div>
                    <div className="flex justify-end items-end gap-2">
                      <textarea
                        className="text-gray-900 rounded-2xl rounded-br-md p-4 w-96 bg-gray-100 resize-none"
                        placeholder="Enter your answer here..."
                        onChange={(e) => {
                          setAnswers({
                            ...answers,
                            [question.questionId]: e.target.value,
                          });
                        }}
                        value={answers[question.questionId]}
                      />
                      <Image
                        src="/icons/avatar-cvs.svg"
                        width={40}
                        height={40}
                        alt="Avatar"
                      />
                    </div>
                  </div>
                ))}
            </div>
          )}
          <div className="flex gap-3">
            <div className="w-fit">
              <Button
                text="Submit Replies"
                variant={
                  isLoading ||
                  isAnsweringQuestions ||
                  !supplierRequestQuestions ||
                  supplierRequestQuestions.length < 1 ||
                  supplierRequestQuestions
                    .filter((question: any) => !question.answered)
                    .some(
                      (question: any) =>
                        !answers[question.questionId] ||
                        answers[question.questionId] === ''
                    )
                    ? 'disabled'
                    : 'primary'
                }
                onClick={handleSubmitAnswers}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  ) : (
    <Modal
      title="Thank You for Your Responses!"
      icon={'/icons/sparkles.svg'}
      confirmation={true}
      showCloseButton={true}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4 items-center">
          <span className="text-text-secondary max-w-[640px] text-center">
            Thank you for answering the supplier&apos;s questions. Your
            responses will help them determine if they can meet your
            requirements effectively. We will notify you once the supplier has
            reviewed your answers and made their decision.
          </span>
        </div>
        <Image
          src="/questions-confirmation.png"
          alt="Confirmation"
          width={1024}
          height={300}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </Modal>
  );
};
